import { useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { supabase } from '../../supabase';
import { formatDateTimeInBusinessTZ } from '../../../utils/date';
import { getErrorMessage } from '../../../utils/errorMessage';
import {
  type AdminCart,
  CART_DETAIL_SELECT,
  cartOwedCents,
  cartReceivedCents,
  cartStatusLabel,
  cartWalletCreditCents,
  payerName,
  refundableCents,
  refundWholeCart,
} from '../../utils/carts';

/** One invoice's share of the cart, in the shape the page's refund modal opens with. */
export interface CartPaymentForRefund {
  id: string;
  amount: number;
  refunded_amount: number;
  payment_method: string;
  status: string;
  created_at: string;
  student_name: string;
  program_name: string;
  paid_by_parent_id: string | null;
  cart_id: string;
}

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

const SOURCE_LABEL: Record<string, string> = {
  stripe_payment_intent: 'Stripe',
  zelle_transaction: 'Zelle',
  zelle_email: 'Zelle',
};

/**
 * A bundle payment as one object: who paid, how, every money event that landed on it (a
 * Zelle cart can take several), and each invoice's share -- refundable one at a time, or all
 * at once for a card payment.
 */
export function CartDetailModal({ cartId, onClose, onRefundPayment, onChanged }: {
  cartId: string | null;
  onClose: () => void;
  /** Opens the page's own refund modal for one invoice's share. */
  onRefundPayment: (payment: CartPaymentForRefund) => void;
  /** Payments or invoices changed; the page should reload what it shows. */
  onChanged: () => void;
}) {
  const [cart, setCart] = useState<AdminCart | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [refundMessage, setRefundMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!cartId) return;
    let live = true;
    setCart(null);
    setLoadError(null);
    (async () => {
      const { data, error } = await supabase.from('payment_carts').select(CART_DETAIL_SELECT).eq('id', cartId).maybeSingle();
      if (!live) return;
      if (error) setLoadError(getErrorMessage(error));
      else if (!data) setLoadError('Bundle payment not found.');
      else setCart(data as unknown as AdminCart);
    })();
    return () => { live = false; };
  }, [cartId, reloadKey]);

  const close = () => {
    setConfirming(false);
    setNote('');
    setRefundMessage(null);
    onClose();
  };

  const items = [...(cart?.payment_cart_items ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const payments = cart?.payments ?? [];
  const paymentsFor = (invoiceId: string) => payments.filter((p) => p.invoice_id === invoiceId);
  const wholeRefundableCents = payments.reduce(
    (sum, p) => sum + (['completed', 'partially_refunded'].includes(p.status) ? refundableCents(p) : 0), 0);
  const walletCredit = cart ? cartWalletCreditCents(cart) : 0;

  const runWholeRefund = async () => {
    if (!cart) return;
    setRefunding(true);
    setRefundMessage(null);
    try {
      const result = await refundWholeCart(payments, note.trim() || `Whole bundle payment ${cart.id} refunded`);
      const total = result.refunded.reduce((sum, r) => sum + r.cents, 0);
      setRefundMessage(result.failed
        ? `Refunded ${usd(total)} across ${result.refunded.length} invoice(s), then stopped: ${result.failed.message}. Run it again to refund what is left.`
        : `Refunded ${usd(total)} across ${result.refunded.length} invoice(s).`);
      setConfirming(false);
      setReloadKey((k) => k + 1);
      onChanged();
    } catch (err) {
      setRefundMessage(getErrorMessage(err));
    } finally {
      setRefunding(false);
    }
  };

  return (
    <Modal isOpen={cartId !== null} onClose={close} title="Bundle payment">
      {loadError ? (
        <p className="text-sm text-red-600" role="alert">{loadError}</p>
      ) : !cart ? (
        <p className="text-sm text-gray-500" data-testid="cart-detail-loading">Loading…</p>
      ) : (
        <div className="space-y-5 text-sm" data-testid="cart-detail">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-gray-500">Paid by</span>
            <span className="text-gray-900">{payerName(cart)}{cart.parent?.email ? ` (${cart.parent.email})` : ''}</span>
            <span className="text-gray-500">How</span>
            <span className="text-gray-900">
              {cart.kind === 'zelle' ? `Zelle, code ${cart.memo_code}` : cart.kind === 'ach' ? 'Bank (ACH)' : 'Card'}
              {cart.payment_intent_id ? <span className="block font-mono text-xs text-gray-500">{cart.payment_intent_id}</span> : null}
            </span>
            <span className="text-gray-500">Status</span>
            <span className="text-gray-900" data-testid="cart-detail-status">{cartStatusLabel(cart)}</span>
            <span className="text-gray-500">Opened</span>
            <span className="text-gray-900">{formatDateTimeInBusinessTZ(cart.created_at)}</span>
            <span className="text-gray-500">Received</span>
            <span className="text-gray-900">
              {usd(cartReceivedCents(cart))}
              {cart.fee_cents > 0 ? ` (incl. ${usd(cart.fee_cents)} card fee)` : ''}
            </span>
            <span className="text-gray-500">Still owed</span>
            <span className="text-gray-900">{usd(cartOwedCents(cart))}</span>
          </div>

          {(cart.payment_cart_fundings ?? []).length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Money received</h3>
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded" data-testid="cart-detail-fundings">
                {(cart.payment_cart_fundings ?? []).map((f) => (
                  <li key={f.id ?? f.source_id} className="px-3 py-2 flex flex-wrap justify-between gap-2">
                    <span>
                      {f.received_at ? formatDateTimeInBusinessTZ(f.received_at) : ''} · {SOURCE_LABEL[f.source_type ?? ''] ?? f.source_type}
                      <span className="ml-1 font-mono text-xs text-gray-500">{f.source_id}</span>
                    </span>
                    <span className="text-gray-900">
                      {usd(f.amount_cents)}
                      {f.residue_cents > 0 ? ` (${usd(f.residue_cents)} to BHCS Cash)` : ''}
                      {f.allocation_rule ? <span className="ml-1 text-xs text-gray-500">[{f.allocation_rule}]</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="font-medium text-gray-900 mb-1">Invoices</h3>
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded" data-testid="cart-detail-invoices">
              {items.map((item) => {
                const student = item.invoices?.enrollments?.students;
                const studentName = student ? `${student.first_name} ${student.last_name}` : 'Student';
                const programName = item.invoices?.enrollments?.programs?.name ?? 'Program';
                return (
                  <li key={item.invoice_id} className="px-3 py-2">
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="text-gray-900">
                        {studentName} · {programName}
                        {item.invoices?.memo_code ? <span className="ml-1 font-mono text-xs text-gray-500">{item.invoices.memo_code}</span> : null}
                      </span>
                      <span className="text-gray-500">invoice status: {item.invoices?.status ?? 'deleted'}</span>
                    </div>
                    {paymentsFor(item.invoice_id).map((p) => (
                      <div key={p.id} className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span className="text-gray-600">
                          Paid {usd(Math.round(p.amount * 100))}
                          {(p.refunded_amount ?? 0) > 0 ? `, refunded ${usd(Math.round((p.refunded_amount ?? 0) * 100))}` : ''} · {p.status.replace(/_/g, ' ')}
                        </span>
                        {['completed', 'partially_refunded'].includes(p.status) && refundableCents(p) > 0 && (
                          <button
                            type="button"
                            className="px-2 py-1 font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                            data-testid={`cart-refund-${p.id}`}
                            onClick={() => onRefundPayment({
                              id: p.id,
                              amount: p.amount,
                              refunded_amount: p.refunded_amount ?? 0,
                              payment_method: p.payment_method,
                              status: p.status,
                              created_at: cart.created_at,
                              student_name: studentName,
                              program_name: programName,
                              paid_by_parent_id: p.paid_by,
                              cart_id: cart.id,
                            })}
                          >
                            Refund this invoice&apos;s share
                          </button>
                        )}
                      </div>
                    ))}
                    {paymentsFor(item.invoice_id).length === 0 && (
                      <p className="mt-1 text-xs text-gray-500">Nothing from this payment landed on it.</p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {walletCredit > 0 && (
            <p className="text-xs text-gray-600 bg-emerald-50 border border-emerald-100 rounded p-2">
              {usd(walletCredit)} of this payment went to the family&apos;s BHCS Cash. Refunds here do not touch it; reverse it
              from Balances if it should go back.
            </p>
          )}

          {cart.kind === 'card' && wholeRefundableCents > 0 && (
            <div className="border-t border-gray-100 pt-4">
              {!confirming ? (
                <button
                  type="button"
                  className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                  onClick={() => setConfirming(true)}
                  data-testid="cart-refund-all"
                >
                  Refund the whole payment ({usd(wholeRefundableCents)})
                </button>
              ) : (
                <div className="space-y-2" data-testid="cart-refund-all-confirm">
                  <p className="text-gray-700">
                    Refund {usd(wholeRefundableCents)} to the card, every invoice&apos;s remaining share, including its part of the
                    processing fee. Each invoice is walked back on its own.
                  </p>
                  <textarea
                    className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    rows={2}
                    placeholder="Note (why)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={refunding}
                      className="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                      onClick={runWholeRefund}
                      data-testid="cart-refund-all-go"
                    >
                      {refunding ? 'Refunding…' : `Refund ${usd(wholeRefundableCents)}`}
                    </button>
                    <button type="button" className="px-3 py-2 text-sm text-gray-700" onClick={() => setConfirming(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {refundMessage && <p className="text-sm text-gray-800" role="status">{refundMessage}</p>}
        </div>
      )}
    </Modal>
  );
}
